import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Dumbbell, Clock, Zap, Trophy, Plus, Loader2, Play, Pause, Square, GripVertical, Filter, Star, CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DAYS_ORDER = ["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"];

function getIntensityColor(intensity) {
  if (!intensity) return "text-gray-400";
  const i = intensity.toLowerCase();
  if (i === "magas" || i === "high") return "text-red-400";
  if (i === "közepes" || i === "medium") return "text-yellow-400";
  return "text-[#00f260]";
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card !p-3 !rounded-lg text-xs">
      <p className="text-gray-400">{label}</p>
      <p className="text-[#00f260] font-medium">{payload[0]?.value} perc</p>
    </div>
  );
};

const RpeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card !p-3 !rounded-lg text-xs">
      <p className="text-gray-400">{label}</p>
      <p className="text-[#00f260] font-medium">RPE: {payload[0]?.value}</p>
    </div>
  );
};

const formatPlanItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  const sets = item.sets ? `${item.sets}x` : "";
  const reps = item.reps ? `${item.reps}` : "";
  const suffix = sets || reps ? ` (${sets}${reps})` : "";
  return `${item.name || ""}${suffix}`.trim();
};

const TEMPLATE_CATEGORIES = ["Általános", "HIIT", "Erő", "Állóképesség", "Core", "Mobility", "Regeneráció"];

const emptyPlan = {
  id: null,
  day_of_week: DAYS_ORDER[0],
  name: "",
  duration_min: "",
  intensity: "közepes",
  exercises: "",
  order: 0
};

const emptyLog = {
  name: "",
  date: new Date().toISOString().slice(0, 10),
  duration: "",
  intensity: "közepes",
  notes: "",
  exercises: "",
  rpe: 5
};

const emptySession = {
  name: "",
  intensity: "közepes",
  notes: "",
  exercises: "",
  rpe: 5
};

export default function WorkoutPage() {
  const { api } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logSaving, setLogSaving] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [logForm, setLogForm] = useState(emptyLog);
  const [logSearch, setLogSearch] = useState("");
  const [logIntensityFilter, setLogIntensityFilter] = useState("all");
  const [logDateFrom, setLogDateFrom] = useState("");
  const [logDateTo, setLogDateTo] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateCategory, setTemplateCategory] = useState("Általános");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [showFavorites, setShowFavorites] = useState(false);
  const [trendView, setTrendView] = useState("weekly");
  const [completingId, setCompletingId] = useState(null);
  const [rpePopup, setRpePopup] = useState(null);
  const [rpeValue, setRpeValue] = useState(6);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiPlanLoading, setAiPlanLoading] = useState(false);
  const [aiPlanApplying, setAiPlanApplying] = useState(false);
  const [aiPlanFocus, setAiPlanFocus] = useState("Általános");
  const [aiPlanDuration, setAiPlanDuration] = useState(45);
  const [aiPlanDays, setAiPlanDays] = useState(4);
  const [aiPlanPhase, setAiPlanPhase] = useState("");
  const [aiPlanCycleWeeks, setAiPlanCycleWeeks] = useState("");
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [planSaving, setPlanSaving] = useState(false);
  const [planDeleting, setPlanDeleting] = useState(false);
  const [dragState, setDragState] = useState({ id: null, day: null });
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [sessionStatus, setSessionStatus] = useState("idle");
  const [sessionForm, setSessionForm] = useState(emptySession);
  const [sessionSaving, setSessionSaving] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLogsLoading(true);
    setTemplatesLoading(true);
    Promise.all([
      api().get("/workouts"),
      api().get("/workouts/logs?days=30"),
      api().get("/workout-templates")
    ])
      .then(([wRes, lRes, tRes]) => {
        setWorkouts(wRes.data);
        setLogs(lRes.data);
        setTemplates(tRes.data || []);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        setLogsLoading(false);
        setTemplatesLoading(false);
      });
  }, [api]);

  useEffect(() => {
    if (sessionStatus !== "running") return;
    const interval = setInterval(() => setSessionSeconds((prev) => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [sessionStatus]);

  const getNextOrder = useCallback((day) => {
    const dayOrders = workouts.filter(w => w.day_of_week === day).map(w => w.order ?? 0);
    return dayOrders.length ? Math.max(...dayOrders) + 1 : 0;
  }, [workouts]);

  useEffect(() => {
    if (planForm.id) return;
    setPlanForm(prev => ({ ...prev, order: getNextOrder(prev.day_of_week) }));
  }, [workouts, planForm.id, getNextOrder]);

  const generatePlan = () => {
    setGenerating(true);
    api().post("/ai/recommendation", { context: "workout" })
      .then(r => {
        toast.success("Új edzésterv javaslat generálva!");
        toast.info(r.data.recommendation?.slice(0, 150) + "...");
      })
      .catch(() => toast.error("Hiba az edzésterv generálásánál"))
      .finally(() => setGenerating(false));
  };

  const updateLogField = (field, value) => {
    setLogForm(prev => ({ ...prev, [field]: value }));
  };

  const resetLogForm = () => {
    setLogForm({ ...emptyLog, date: new Date().toISOString().slice(0, 10) });
    setEditingLogId(null);
  };

  const handleLogSubmit = async (e) => {
    e.preventDefault();
    if (!logForm.name.trim() || !logForm.duration) {
      toast.error("Add meg az edzés nevét és időtartamát.");
      return;
    }
    setLogSaving(true);
    const intensityPct = logForm.intensity === "magas" ? 85 : logForm.intensity === "közepes" ? 65 : 45;
    const exercises = logForm.exercises
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean)
      .map(name => ({ name, sets: 1, reps: "10", intensity_pct: intensityPct }));

    try {
      const payload = {
        name: logForm.name.trim(),
        date: logForm.date,
        duration_min: Number(logForm.duration),
        intensity: logForm.intensity,
        notes: logForm.notes,
        exercises,
        rpe: logForm.rpe ? Number(logForm.rpe) : null
      };
      if (editingLogId) {
        const res = await api().put(`/workouts/logs/${editingLogId}`, payload);
        setLogs(prev => prev.map(log => log.id === editingLogId ? res.data : log));
        toast.success("Edzés napló frissítve!");
      } else {
        const res = await api().post("/workouts/logs", payload);
        setLogs(prev => [res.data, ...prev]);
        toast.success("Edzés naplózva!");
      }
      resetLogForm();
    } catch {
      toast.error("Nem sikerült menteni az edzést");
    } finally {
      setLogSaving(false);
    }
  };

  const handleLogEdit = (log) => {
    setEditingLogId(log.id);
    setLogForm({
      name: log.name || "",
      date: log.date || new Date().toISOString().slice(0, 10),
      duration: log.duration_min || "",
      intensity: log.intensity || "közepes",
      notes: log.notes || "",
      exercises: (log.exercises || []).map(ex => ex.name).join("\n"),
      rpe: log.rpe || 5
    });
  };

  const handleLogDelete = async (logId) => {
    try {
      await api().delete(`/workouts/logs/${logId}`);
      setLogs(prev => prev.filter(log => log.id !== logId));
      if (editingLogId === logId) resetLogForm();
      toast.success("Edzés napló törölve!");
    } catch {
      toast.error("Nem sikerült törölni a naplót");
    }
  };

  const handleDragStart = (workout) => {
    setDragState({ id: workout.id, day: workout.day_of_week });
  };

  const handleDragEnd = () => {
    setDragState({ id: null, day: null });
  };

  const handleReorder = async (day, targetId) => {
    if (!dragState.id || dragState.day !== day || dragState.id === targetId) return;
    const dayItems = workouts
      .filter(w => w.day_of_week === day)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const fromIndex = dayItems.findIndex(w => w.id === dragState.id);
    const toIndex = dayItems.findIndex(w => w.id === targetId);
    if (fromIndex < 0 || toIndex < 0) return;
    const updatedItems = [...dayItems];
    const [moved] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, moved);

    const updatedMap = new Map(updatedItems.map((item, index) => [item.id, { ...item, order: index }]));
    setWorkouts(prev => prev.map(w => updatedMap.get(w.id) || w));
    try {
      await Promise.all(updatedItems.map((item, index) => api().put(`/workouts/${item.id}`, {
        name: item.name,
        day_of_week: item.day_of_week,
        duration_min: item.duration_min,
        intensity: item.intensity,
        exercises: item.exercises || [],
        order: index
      })));
      toast.success("Sorrend frissítve!");
    } catch {
      toast.error("Nem sikerült menteni a sorrendet");
    } finally {
      handleDragEnd();
    }
  };

  const handleDropAtEnd = async (day) => {
    if (!dragState.id || dragState.day !== day) return;
    const dayItems = workouts
      .filter(w => w.day_of_week === day)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const fromIndex = dayItems.findIndex(w => w.id === dragState.id);
    if (fromIndex < 0) return;
    const updatedItems = [...dayItems];
    const [moved] = updatedItems.splice(fromIndex, 1);
    updatedItems.push(moved);

    const updatedMap = new Map(updatedItems.map((item, index) => [item.id, { ...item, order: index }]));
    setWorkouts(prev => prev.map(w => updatedMap.get(w.id) || w));
    try {
      await Promise.all(updatedItems.map((item, index) => api().put(`/workouts/${item.id}`, {
        name: item.name,
        day_of_week: item.day_of_week,
        duration_min: item.duration_min,
        intensity: item.intensity,
        exercises: item.exercises || [],
        order: index
      })));
      toast.success("Sorrend frissítve!");
    } catch {
      toast.error("Nem sikerült menteni a sorrendet");
    } finally {
      handleDragEnd();
    }
  };

  const updatePlanField = (field, value) => {
    setPlanForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePlanDayChange = (value) => {
    setPlanForm(prev => ({
      ...prev,
      day_of_week: value,
      order: prev.id && prev.day_of_week === value ? prev.order : getNextOrder(value)
    }));
  };

  const resetPlanForm = (day = DAYS_ORDER[0]) => {
    setPlanForm({ ...emptyPlan, day_of_week: day, order: getNextOrder(day) });
    setTemplateCategory("Általános");
    setSelectedWorkout(null);
  };

  const handlePlanSelect = (day, workout) => {
    setSelectedWorkout(workout || null);
    setPlanForm({
      id: workout?.id || null,
      day_of_week: day,
      name: workout?.name || "",
      duration_min: workout?.duration_min || "",
      intensity: workout?.intensity || "közepes",
      exercises: workout?.exercises?.map(ex => ex.name).join("\n") || "",
      order: workout?.order ?? getNextOrder(day)
    });
  };

  const handlePlanSave = async () => {
    if (!planForm.name.trim() || !planForm.duration_min) {
      toast.error("Add meg az edzés nevét és időtartamát.");
      return;
    }
    setPlanSaving(true);
    const intensityPct = planForm.intensity === "magas" ? 85 : planForm.intensity === "közepes" ? 65 : 45;
    const exercises = planForm.exercises
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean)
      .map(name => ({ name, sets: 1, reps: "10", intensity_pct: intensityPct }));

    const payload = {
      name: planForm.name.trim(),
      day_of_week: planForm.day_of_week,
      duration_min: Number(planForm.duration_min),
      intensity: planForm.intensity,
      exercises
    };

    try {
      const targetOrder = planForm.id ? planForm.order : getNextOrder(planForm.day_of_week);
      const payloadWithOrder = { ...payload, order: targetOrder };
      let res;
      if (planForm.id) {
        res = await api().put(`/workouts/${planForm.id}`, payloadWithOrder);
      } else {
        res = await api().post("/workouts", payloadWithOrder);
      }
      const updated = res.data;
      setWorkouts(prev => planForm.id ? prev.map(w => w.id === updated.id ? updated : w) : [...prev, updated]);
      toast.success("Edzésterv mentve!");
      setPlanForm({
        id: updated.id,
        day_of_week: updated.day_of_week,
        name: updated.name,
        duration_min: updated.duration_min,
        intensity: updated.intensity,
        exercises: updated.exercises?.map(ex => ex.name).join("\n") || "",
        order: updated.order ?? 0
      });
      setSelectedWorkout(updated);
    } catch {
      toast.error("Nem sikerült menteni az edzéstervet");
    } finally {
      setPlanSaving(false);
    }
  };

  const handlePlanDelete = async () => {
    if (!planForm.id) {
      toast.error("Nincs kiválasztott edzés a törléshez");
      return;
    }
    setPlanDeleting(true);
    try {
      await api().delete(`/workouts/${planForm.id}`);
      setWorkouts(prev => prev.filter(w => w.id !== planForm.id));
      toast.success("Edzés törölve!");
      resetPlanForm(planForm.day_of_week);
    } catch {
      toast.error("Nem sikerült törölni az edzést");
    } finally {
      setPlanDeleting(false);
    }
  };

  const handleTemplateSave = async () => {
    if (!planForm.name.trim()) {
      toast.error("Add meg a sablon nevét.");
      return;
    }
    if (!planForm.duration_min) {
      toast.error("Add meg az időtartamot a sablonhoz.");
      return;
    }
    setTemplateSaving(true);
    const intensityPct = planForm.intensity === "magas" ? 85 : planForm.intensity === "közepes" ? 65 : 45;
    const exercises = planForm.exercises
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean)
      .map(name => ({ name, sets: 1, reps: "10", intensity_pct: intensityPct }));

    try {
      const payload = {
        name: planForm.name.trim(),
        duration_min: Number(planForm.duration_min),
        intensity: planForm.intensity,
        category: templateCategory,
        is_favorite: false,
        exercises
      };
      const res = await api().post("/workout-templates", payload);
      setTemplates(prev => [res.data, ...prev]);
      toast.success("Sablon mentve!");
    } catch {
      toast.error("Nem sikerült menteni a sablont");
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleTemplateApply = (template) => {
    setPlanForm(prev => ({
      ...prev,
      id: null,
      name: template.name,
      duration_min: template.duration_min,
      intensity: template.intensity,
      exercises: (template.exercises || []).map(ex => ex.name).join("\n"),
      order: getNextOrder(prev.day_of_week)
    }));
    setTemplateCategory(template.category || "Általános");
    toast.success("Sablon betöltve");
  };

  const handleTemplateDelete = async (templateId) => {
    try {
      await api().delete(`/workout-templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success("Sablon törölve");
    } catch {
      toast.error("Nem sikerült törölni a sablont");
    }
  };

  const handleTemplateToggleFavorite = async (template) => {
    try {
      const res = await api().put(`/workout-templates/${template.id}`, {
        is_favorite: !template.is_favorite
      });
      setTemplates(prev => prev.map(t => t.id === template.id ? res.data : t));
      toast.success("Kedvenc frissítve");
    } catch {
      toast.error("Nem sikerült frissíteni a kedvencet");
    }
  };

  const handleTemplateCategoryUpdate = async (template, category) => {
    try {
      const res = await api().put(`/workout-templates/${template.id}`, { category });
      setTemplates(prev => prev.map(t => t.id === template.id ? res.data : t));
      toast.success("Kategória frissítve");
    } catch {
      toast.error("Nem sikerült frissíteni a kategóriát");
    }
  };

  const handleAiPlanGenerate = async () => {
    const fallback = {
      title: `${aiPlanDuration} perces ${aiPlanFocus} terv`,
      focus: aiPlanFocus,
      warmup: ["Mobilitás 8 perc", "Könnyű kardió 5 perc"],
      main: ["Fő blokk 4x8", "Core 10 perc"],
      cooldown: ["Levezetés 5 perc", "Nyújtás 6 perc"],
      weekly_split: [
        { day: "Hétfő", focus: aiPlanFocus, duration_min: Number(aiPlanDuration) || 45 },
        { day: "Szerda", focus: "Technika", duration_min: 35 },
        { day: "Péntek", focus: "Regeneráció", duration_min: 30 }
      ],
      tips: ["Tarts pihenőnapot két intenzív nap között."]
    };
    setAiPlan(fallback);
    setAiPlanLoading(true);
    try {
      const res = await api().post("/ai/workout-plan", {
        focus: aiPlanFocus,
        duration_min: Number(aiPlanDuration) || 45,
        days_per_week: Number(aiPlanDays) || 4,
        phase: aiPlanPhase || null,
        cycle_weeks: Number(aiPlanCycleWeeks) || null
      });
      setAiPlan(res.data?.title ? res.data : fallback);
      toast.success("AI edzésterv elkészült!");
    } catch {
      setAiPlan(fallback);
      toast.error("Az AI edzésterv jelenleg nem elérhető, alap tervet mutatunk.");
    } finally {
      setAiPlanLoading(false);
    }
  };

  const handleApplyAiPlan = async () => {
    if (!aiPlan?.weekly_split?.length) {
      toast.error("Nincs alkalmazható edzésterv.");
      return;
    }
    setAiPlanApplying(true);
    const toExercise = (item) => {
      if (typeof item === "string") return { name: item, sets: 1, reps: "-", intensity_pct: 50 };
      return { name: item.name || String(item), sets: item.sets || 1, reps: item.reps || "-", intensity_pct: item.intensity_pct || 50 };
    };
    const allExercises = [
      ...(aiPlan.warmup || []).map(toExercise),
      ...(aiPlan.main || []).map(toExercise),
      ...(aiPlan.cooldown || []).map(toExercise)
    ];
    let created = 0;
    try {
      for (const split of aiPlan.weekly_split) {
        const dayName = typeof split === "string" ? split : (split.day || split.name);
        if (!dayName || !DAYS_ORDER.includes(dayName)) continue;
        const focus = typeof split === "string" ? aiPlan.focus : (split.focus || aiPlan.focus || "Általános");
        const dur = typeof split === "string" ? (aiPlan.duration_min || 45) : (split.duration_min || aiPlan.duration_min || 45);
        const order = getNextOrder(dayName) + created;
        const payload = {
          name: `${focus} (AI)`,
          day_of_week: dayName,
          duration_min: Number(dur),
          intensity: dur >= 50 ? "magas" : dur >= 30 ? "közepes" : "alacsony",
          exercises: allExercises,
          order
        };
        const res = await api().post("/workouts", payload);
        setWorkouts(prev => [...prev, res.data]);
        created++;
      }
      toast.success(`${created} edzés hozzáadva a heti tervhez!`);
    } catch {
      toast.error("Hiba az edzések hozzáadásakor.");
    } finally {
      setAiPlanApplying(false);
    }
  };

  const handleMarkCompleted = (workout, e) => {
    e.stopPropagation();
    setRpePopup(workout);
    setRpeValue(6);
  };

  const handleConfirmComplete = async () => {
    if (!rpePopup) return;
    const workout = rpePopup;
    setCompletingId(workout.id);
    setRpePopup(null);
    try {
      const logPayload = {
        name: workout.name,
        date: new Date().toISOString().slice(0, 10),
        duration_min: workout.duration_min || 0,
        intensity: workout.intensity || "közepes",
        exercises: workout.exercises || [],
        notes: `Elvégezve a heti tervből (${workout.day_of_week})`,
        rpe: rpeValue
      };
      const res = await api().post("/workouts/logs", logPayload);
      setLogs(prev => [res.data, ...prev]);
      toast.success(`"${workout.name}" naplózva (RPE: ${rpeValue})!`);
    } catch {
      toast.error("Hiba a naplózásnál.");
    } finally {
      setCompletingId(null);
    }
  };

  const updateSessionField = (field, value) => {
    setSessionForm(prev => ({ ...prev, [field]: value }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const sessionMinutes = Math.max(1, Math.round(sessionSeconds / 60));

  const handleSessionStart = () => {
    if (sessionStatus === "idle" || sessionStatus === "stopped") {
      setSessionSeconds(0);
      setSessionSummary(null);
    }
    setSessionStatus("running");
  };

  const handleSessionPause = () => setSessionStatus("paused");
  const handleSessionResume = () => setSessionStatus("running");
  const handleSessionStop = () => {
    setSessionStatus("stopped");
    setSessionSummary({
      duration_min: sessionMinutes,
      duration_sec: sessionSeconds,
      stopped_at: new Date().toISOString()
    });
  };

  const handleSessionReset = () => {
    setSessionStatus("idle");
    setSessionSeconds(0);
    setSessionForm(emptySession);
    setSessionSummary(null);
  };

  const handleSessionSave = async () => {
    if (!sessionForm.name.trim()) {
      toast.error("Add meg az edzés nevét.");
      return;
    }
    setSessionSaving(true);
    const intensityPct = sessionForm.intensity === "magas" ? 85 : sessionForm.intensity === "közepes" ? 65 : 45;
    const exercises = sessionForm.exercises
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean)
      .map(name => ({ name, sets: 1, reps: "10", intensity_pct: intensityPct }));

    try {
      const payload = {
        name: sessionForm.name.trim(),
        date: new Date().toISOString().slice(0, 10),
        duration_min: sessionMinutes,
        intensity: sessionForm.intensity,
        notes: sessionForm.notes,
        exercises,
        rpe: sessionForm.rpe ? Number(sessionForm.rpe) : null
      };
      const res = await api().post("/workouts/logs", payload);
      setLogs(prev => [res.data, ...prev]);
      toast.success("Session mentve a naplóba!");
      handleSessionReset();
    } catch {
      toast.error("Nem sikerült menteni a sessiont");
    } finally {
      setSessionSaving(false);
    }
  };

  const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

  const downloadCsv = (filename, headers, rows) => {
    const csv = [headers.join(","), ...rows.map(row => headers.map(key => csvEscape(row[key])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const normalizedLogSearch = logSearch.trim().toLowerCase();
  const filteredLogs = logs.filter((log) => {
    const matchesSearch = !normalizedLogSearch || [log.name, log.notes, ...(log.exercises || []).map(ex => ex.name)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedLogSearch);
    const matchesIntensity = logIntensityFilter === "all" || (log.intensity || "").toLowerCase() === logIntensityFilter;
    const matchesFrom = !logDateFrom || log.date >= logDateFrom;
    const matchesTo = !logDateTo || log.date <= logDateTo;
    return matchesSearch && matchesIntensity && matchesFrom && matchesTo;
  });

  const filteredTemplates = templates
    .map(t => ({ ...t, category: t.category || "Általános", is_favorite: !!t.is_favorite }))
    .filter(t => (templateFilter === "all" || t.category === templateFilter) && (!showFavorites || t.is_favorite))
    .sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0));

  const groupedWorkouts = DAYS_ORDER.map(day => {
    const items = workouts
      .filter(w => w.day_of_week === day)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { day, workouts: items };
  });

  const weeklyData = groupedWorkouts.map(({ day, workouts }) => ({
    day: day.slice(0, 2),
    duration: workouts.reduce((sum, w) => sum + (w.duration_min || 0), 0),
    count: workouts.length
  }));

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 6);
  const weekStartStr = weekStart.toISOString().slice(0, 10);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthStartStr = monthStart.toISOString().slice(0, 10);

  const weeklyLogs = logs.filter(log => log.date >= weekStartStr && log.date <= todayStr);
  const monthlyLogs = logs.filter(log => log.date >= monthStartStr && log.date <= todayStr);
  const selectedLogs = trendView === "monthly" ? monthlyLogs : weeklyLogs;

  const totalSessions = selectedLogs.length;
  const totalMinutes = selectedLogs.reduce((sum, log) => sum + (log.duration_min || 0), 0);
  const rpeValues = selectedLogs.map(log => log.rpe).filter(Boolean);
  const avgRpe = rpeValues.length ? (rpeValues.reduce((sum, rpe) => sum + rpe, 0) / rpeValues.length) : 0;
  const loadScore = selectedLogs.reduce((sum, log) => sum + (log.duration_min || 0) * (log.rpe || 5), 0);
  const rpeTrend = [...selectedLogs]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(log => ({
      date: log.date.slice(5),
      rpe: log.rpe || 0,
      load: (log.duration_min || 0) * (log.rpe || 5)
    }));

  const zoneCounts = selectedLogs.reduce((acc, log) => {
    const score = (log.duration_min || 0) * (log.rpe || 5);
    if (score < 200) acc.low += 1;
    else if (score < 400) acc.medium += 1;
    else acc.high += 1;
    return acc;
  }, { low: 0, medium: 0, high: 0 });

  const periodLabel = trendView === "monthly" ? "Havi" : "Heti";
  const periodRange = trendView === "monthly" ? `${monthStartStr} - ${todayStr}` : `${weekStartStr} - ${todayStr}`;
  const reportText = `Athlevity ${periodLabel.toLowerCase()} riport (${periodRange})\nEdzések: ${totalSessions}\nÖsszidő: ${totalMinutes} perc\nÁtlag RPE: ${avgRpe ? avgRpe.toFixed(1) : "-"}\nTerhelés pont: ${loadScore}`;

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      toast.success("Riport kimásolva!");
    } catch {
      toast.error("Nem sikerült kimásolni a riportot");
    }
  };

  const handleLogsExport = () => {
    const rows = filteredLogs.map(log => ({
      date: log.date,
      name: log.name,
      duration_min: log.duration_min,
      intensity: log.intensity,
      rpe: log.rpe || "",
      notes: log.notes || "",
      exercises: (log.exercises || []).map(ex => ex.name).join(" | ")
    }));
    downloadCsv("athlevity_workout_logs.csv", ["date", "name", "duration_min", "intensity", "rpe", "notes", "exercises"], rows);
  };

  return (
    <div className="space-y-6 md:pt-20" data-testid="workout-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Edzés</h1>
        <Dumbbell className="text-[#00f260]" size={24} />
      </div>

      <Tabs defaultValue="programs" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 rounded-xl p-1 w-full" data-testid="workout-tabs">
          <TabsTrigger value="programs" data-testid="workout-tab-programs" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Programok</TabsTrigger>
          <TabsTrigger value="planner" data-testid="workout-tab-planner" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Tervező</TabsTrigger>
          <TabsTrigger value="session" data-testid="workout-tab-session" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Időzítő</TabsTrigger>
          <TabsTrigger value="history" data-testid="workout-tab-history" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#00f260] data-[state=active]:text-black">Előzmények</TabsTrigger>
        </TabsList>

        {/* Programs */}
        <TabsContent value="programs" className="space-y-4 mt-4">
          {/* RPE Popup */}
          {rpePopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setRpePopup(null)} data-testid="rpe-popup-overlay">
              <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-5 w-80 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="rpe-popup">
                <p className="text-sm text-white font-medium mb-1">{rpePopup.name}</p>
                <p className="text-[10px] text-gray-500 mb-4">{rpePopup.duration_min} perc &middot; {rpePopup.intensity} &middot; {rpePopup.day_of_week}</p>
                <p className="text-xs text-gray-400 mb-2">RPE (nehézségi érték)</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    data-testid="rpe-slider"
                    type="range"
                    min="1"
                    max="10"
                    value={rpeValue}
                    onChange={e => setRpeValue(Number(e.target.value))}
                    className="flex-1 accent-[#00f260] h-2"
                  />
                  <span data-testid="rpe-value" className={`text-xl font-bold min-w-[2rem] text-center ${rpeValue <= 4 ? "text-green-400" : rpeValue <= 7 ? "text-amber-400" : "text-red-400"}`}>
                    {rpeValue}
                  </span>
                </div>
                <div className="flex justify-between text-[10px] text-gray-600 mb-4 px-1">
                  <span>Könnyű</span>
                  <span>Közepes</span>
                  <span>Maximális</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setRpePopup(null)} className="flex-1 px-3 py-2 rounded-xl text-xs text-gray-400 bg-white/5 hover:bg-white/10 transition-colors" data-testid="rpe-cancel-btn">
                    Mégse
                  </button>
                  <button onClick={handleConfirmComplete} className="flex-1 neon-btn text-xs flex items-center justify-center gap-1.5" data-testid="rpe-confirm-btn">
                    <CheckCircle2 size={14} />
                    Naplózás
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Schedule */}
          <GlassCard testId="weekly-schedule-card" className="fade-in-up">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Heti ütemezés</p>
            <p className="text-[10px] text-gray-500 mb-4" data-testid="schedule-hint">Húzd és dobd az edzéseket a sorrendhez</p>
            <div className="space-y-4">
              {groupedWorkouts.map(({ day, workouts }) => (
                <div key={day} className="space-y-2" data-testid={`day-group-${day}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 w-20 font-medium">{day}</span>
                    <span className="text-[10px] text-gray-500">{workouts.length ? `${workouts.length} edzés` : "Pihenő"}</span>
                  </div>
                  <div
                    className="space-y-2"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropAtEnd(day)}
                    data-testid={`schedule-drop-${day}`}
                  >
                    {workouts.length ? workouts.map((workout, index) => (
                      <div
                        key={workout.id}
                        draggable
                        onDragStart={() => handleDragStart(workout)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleReorder(day, workout.id)}
                        className={`flex items-center justify-between p-3 rounded-xl transition-all cursor-pointer ${
                          selectedWorkout?.id === workout.id ? "border border-[#00f260]/50 bg-white/10" : "bg-white/5 hover:bg-white/10"
                        } ${dragState.id === workout.id ? "opacity-70" : ""}`}
                        onClick={() => handlePlanSelect(day, workout)}
                        data-testid={`schedule-${day}-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical size={14} className="text-gray-500" data-testid={`drag-handle-${workout.id}`} />
                          <div>
                            <p className="text-sm text-white">{workout.name}</p>
                            <p className="text-[10px] text-gray-500">{workout.duration_min} perc • {workout.intensity}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`complete-btn-${workout.id}`}
                            onClick={(e) => handleMarkCompleted(workout, e)}
                            disabled={completingId === workout.id}
                            className="p-1.5 rounded-lg hover:bg-[#00f260]/20 text-gray-400 hover:text-[#00f260] transition-all"
                            title="Elvégezve – hozzáadás a naplóhoz"
                          >
                            {completingId === workout.id ? <Loader2 size={14} className="animate-spin text-[#00f260]" /> : <CheckCircle2 size={14} />}
                          </button>
                          <Clock size={12} className="text-gray-500" />
                          <span className="text-xs text-gray-400">{workout.duration_min}'</span>
                        </div>
                      </div>
                    )) : (
                      <div className="text-xs text-gray-500 px-3 py-2 rounded-lg border border-white/5" data-testid={`schedule-empty-${day}`}>
                        Pihenő nap
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Workout Detail */}
          {selectedWorkout && (
            <GlassCard testId="workout-detail-card" className="fade-in-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold text-white">{selectedWorkout.name}</h3>
                <Badge className={`${getIntensityColor(selectedWorkout.intensity)} bg-white/5 border-0 text-xs`}>
                  {selectedWorkout.intensity}
                </Badge>
              </div>
              <div className="space-y-2">
                {selectedWorkout.exercises?.map((ex, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl" data-testid={`exercise-${i}`}>
                    <div>
                      <p className="text-sm text-white">{ex.name}</p>
                      <p className="text-xs text-gray-400">{ex.sets} sorozat x {ex.reps}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap size={12} className={getIntensityColor(ex.intensity_pct > 70 ? "magas" : ex.intensity_pct > 40 ? "közepes" : "alacsony")} />
                      <span className="text-xs text-gray-400">{ex.intensity_pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          <GlassCard testId="workout-plan-form-card" className="fade-in-up">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Edzésterv szerkesztése</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Nap</Label>
                  <select
                    data-testid="plan-day-select"
                    value={planForm.day_of_week}
                    onChange={(e) => handlePlanDayChange(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    {DAYS_ORDER.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Edzés neve</Label>
                  <Input
                    data-testid="plan-name-input"
                    value={planForm.name}
                    onChange={(e) => updatePlanField("name", e.target.value)}
                    placeholder="Pl. HIIT futás"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Időtartam (perc)</Label>
                  <Input
                    data-testid="plan-duration-input"
                    type="number"
                    min="10"
                    step="5"
                    value={planForm.duration_min}
                    onChange={(e) => updatePlanField("duration_min", e.target.value)}
                    placeholder="45"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Intenzitás</Label>
                  <select
                    data-testid="plan-intensity-select"
                    value={planForm.intensity}
                    onChange={(e) => updatePlanField("intensity", e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    <option value="alacsony">Alacsony</option>
                    <option value="közepes">Közepes</option>
                    <option value="magas">Magas</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Sablon kategória</Label>
                  <select
                    data-testid="template-category-select"
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    {TEMPLATE_CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2 md:col-span-3">
                  <Label className="text-gray-400 text-xs">Gyakorlatok (soronként)</Label>
                  <textarea
                    data-testid="plan-exercises-input"
                    value={planForm.exercises}
                    onChange={(e) => updatePlanField("exercises", e.target.value)}
                    placeholder="Pl. 400m intervall\nLevezetés"
                    className="w-full min-h-[90px] bg-black/20 border border-white/10 text-white placeholder:text-gray-500 rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <button
                  data-testid="plan-save-btn"
                  onClick={handlePlanSave}
                  disabled={planSaving}
                  className="neon-btn w-full text-sm flex items-center justify-center gap-2"
                >
                  {planSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  {planForm.id ? "Edzés frissítése" : "Edzés hozzáadása"}
                </button>
                <button
                  data-testid="template-save-btn"
                  onClick={handleTemplateSave}
                  disabled={templateSaving}
                  className="w-full md:w-auto px-6 py-3 rounded-full border border-[#00f260]/40 text-[#00f260] hover:text-white hover:border-white/30 transition-all text-sm"
                >
                  {templateSaving ? "Mentés..." : "Sablon mentése"}
                </button>
                <button
                  data-testid="plan-reset-btn"
                  type="button"
                  onClick={() => resetPlanForm(planForm.day_of_week)}
                  className="w-full md:w-auto px-6 py-3 rounded-full border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all text-sm"
                >
                  Új edzés
                </button>
                <button
                  data-testid="plan-delete-btn"
                  type="button"
                  onClick={handlePlanDelete}
                  disabled={planDeleting}
                  className="w-full md:w-auto px-6 py-3 rounded-full border border-red-400/30 text-red-300 hover:text-red-200 hover:border-red-300 transition-all text-sm"
                >
                  {planDeleting ? "Törlés..." : "Törlés"}
                </button>
              </div>
            </div>
          </GlassCard>

          <GlassCard testId="workout-templates-card" className="fade-in-up fade-in-up-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider">Edzés sablonok</p>
              <span className="text-[10px] text-gray-500" data-testid="template-count">{templates.length} sablon</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                data-testid="template-filter-all"
                onClick={() => setTemplateFilter("all")}
                className={`px-3 py-1 rounded-full text-xs border ${templateFilter === "all" ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-400"}`}
              >
                Összes
              </button>
              {TEMPLATE_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  data-testid={`template-filter-${cat}`}
                  onClick={() => setTemplateFilter(cat)}
                  className={`px-3 py-1 rounded-full text-xs border ${templateFilter === cat ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-400"}`}
                >
                  {cat}
                </button>
              ))}
              <button
                data-testid="template-filter-favorites"
                onClick={() => setShowFavorites((prev) => !prev)}
                className={`px-3 py-1 rounded-full text-xs border ${showFavorites ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-400"}`}
              >
                Kedvencek
              </button>
            </div>
            {templatesLoading ? (
              <div className="text-sm text-gray-500" data-testid="template-loading">Betöltés...</div>
            ) : filteredTemplates.length > 0 ? (
              <div className="space-y-3">
                {filteredTemplates.map((template) => (
                  <div key={template.id} className="p-3 bg-white/5 rounded-xl" data-testid={`template-item-${template.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-white">{template.name}</p>
                        <p className="text-xs text-gray-400">{template.duration_min} perc • {template.intensity}</p>
                        <p className="text-[10px] text-gray-500 mt-1">{template.category || "Általános"}</p>
                      </div>
                      <button
                        data-testid={`template-favorite-${template.id}`}
                        onClick={() => handleTemplateToggleFavorite(template)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center border ${template.is_favorite ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-500"}`}
                      >
                        <Star size={14} />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        data-testid={`template-apply-${template.id}`}
                        onClick={() => handleTemplateApply(template)}
                        className="px-3 py-1 rounded-full border border-white/10 text-gray-300 text-xs hover:text-white hover:border-white/30 transition-all"
                      >
                        Betöltés
                      </button>
                      <select
                        data-testid={`template-category-${template.id}`}
                        value={template.category || "Általános"}
                        onChange={(e) => handleTemplateCategoryUpdate(template, e.target.value)}
                        className="bg-black/20 border border-white/10 text-white rounded-full px-3 py-1 text-xs"
                      >
                        {TEMPLATE_CATEGORIES.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <button
                        data-testid={`template-delete-${template.id}`}
                        onClick={() => handleTemplateDelete(template.id)}
                        className="px-3 py-1 rounded-full border border-red-400/30 text-red-300 text-xs hover:text-red-200 hover:border-red-300 transition-all"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500" data-testid="template-empty">Nincs találat a szűrésre.</div>
            )}
          </GlassCard>
        </TabsContent>

        {/* Planner */}
        <TabsContent value="planner" className="space-y-4 mt-4">
          <GlassCard testId="weekly-load-chart" className="fade-in-up">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Heti terhelés</p>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyData}>
                  <XAxis dataKey="day" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="duration" fill="#00f260" radius={[6, 6, 0, 0]} opacity={0.8} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>

          {/* Personal Best */}
          <GlassCard className="fade-in-up fade-in-up-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00f260]/10 flex items-center justify-center">
                <Trophy size={20} className="text-[#00f260]" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Egyéni csúcs</p>
                <p className="text-sm text-white">Heti edzésidő: <span className="text-[#00f260] font-medium">6.2 óra</span></p>
              </div>
            </div>
          </GlassCard>

          <GlassCard testId="ai-workout-plan-card" className="fade-in-up fade-in-up-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">AI edzésterv (részletes)</p>
                <p className="text-[10px] text-gray-500">Válaszd ki a fókuszt és generálj heti vázlatot</p>
              </div>
              <button
                data-testid="ai-plan-generate-btn"
                onClick={handleAiPlanGenerate}
                disabled={aiPlanLoading}
                className="neon-btn text-xs px-5 py-2 flex items-center gap-2"
              >
                {aiPlanLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                AI terv
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Fókusz</Label>
                <select
                  data-testid="ai-plan-focus-select"
                  value={aiPlanFocus}
                  onChange={(e) => setAiPlanFocus(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                >
                  {TEMPLATE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Időtartam (perc)</Label>
                <Input
                  data-testid="ai-plan-duration-input"
                  type="number"
                  min="20"
                  step="5"
                  value={aiPlanDuration}
                  onChange={(e) => setAiPlanDuration(e.target.value)}
                  className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Heti napok</Label>
                <Input
                  data-testid="ai-plan-days-input"
                  type="number"
                  min="2"
                  max="6"
                  value={aiPlanDays}
                  onChange={(e) => setAiPlanDays(e.target.value)}
                  className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>
            </div>

            {/* Periodization options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3" data-testid="ai-plan-periodization-options">
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Edzés fázis (periodizáció)</Label>
                <select
                  data-testid="ai-plan-phase-select"
                  value={aiPlanPhase}
                  onChange={(e) => setAiPlanPhase(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                >
                  <option value="">Nincs (egyszeri terv)</option>
                  <option value="alap">Alap - Aerob alap építés</option>
                  <option value="erő">Erő - Intenzitás fejlesztés</option>
                  <option value="csúcs">Csúcs - Csúcsforma elérés</option>
                  <option value="regeneráció">Regeneráció - Aktív pihenő</option>
                </select>
              </div>
              {aiPlanPhase && (
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Ciklus hossz (hét)</Label>
                  <select
                    data-testid="ai-plan-cycle-weeks"
                    value={aiPlanCycleWeeks}
                    onChange={(e) => setAiPlanCycleWeeks(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    <option value="">Csak aktuális hét</option>
                    <option value="4">4 hét (mikrociklus)</option>
                    <option value="8">8 hét (mezociklus)</option>
                    <option value="12">12 hét (makrociklus)</option>
                    <option value="16">16 hét (verseny felkészülés)</option>
                  </select>
                </div>
              )}
            </div>

            {aiPlan ? (
              <div className="mt-4 space-y-3" data-testid="ai-plan-result">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white font-medium">{aiPlan.title || "AI edzésterv"}</p>
                  <Badge className="bg-white/5 border-0 text-[#00f260] text-xs">{aiPlan.focus || aiPlanFocus}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Bemelegítés</p>
                    <ul className="text-xs text-gray-300 space-y-1" data-testid="ai-plan-warmup">
                      {aiPlan.warmup?.map((item, idx) => (
                        <li key={`warmup-${idx}`}>• {formatPlanItem(item)}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Fő blokk</p>
                    <ul className="text-xs text-gray-300 space-y-1" data-testid="ai-plan-main">
                      {aiPlan.main?.map((item, idx) => (
                        <li key={`main-${idx}`}>• {formatPlanItem(item)}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Levezetés</p>
                    <ul className="text-xs text-gray-300 space-y-1" data-testid="ai-plan-cooldown">
                      {aiPlan.cooldown?.map((item, idx) => (
                        <li key={`cooldown-${idx}`}>• {formatPlanItem(item)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Heti vázlat</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="ai-plan-weekly">
                    {aiPlan.weekly_split?.map((day, idx) => (
                      <div key={`split-${idx}`} className="p-2 rounded-lg bg-white/5 text-xs text-gray-300">
                        <p className="text-white">{typeof day === "string" ? day : (day.day || day.name || "Nap")}</p>
                        <p>{typeof day === "string" ? aiPlan.focus : (day.focus || aiPlan.focus)}</p>
                        <p>{typeof day === "string" ? (aiPlan.duration_min || aiPlanDuration) : (day.duration_min || aiPlan.duration_min || aiPlanDuration)} perc</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Periodization timeline */}
                {aiPlan.periodization?.length > 0 && (
                  <div data-testid="ai-plan-periodization">
                    <p className="text-xs text-gray-400 mb-2">Periodizáció – {aiPlan.cycle_weeks || aiPlanCycleWeeks} hetes ciklus</p>
                    <div className="flex gap-1">
                      {aiPlan.periodization.map((p, idx) => {
                        const colors = { alap: "bg-blue-500/20 border-blue-500/30 text-blue-400", erő: "bg-amber-500/20 border-amber-500/30 text-amber-400", csúcs: "bg-red-500/20 border-red-500/30 text-red-400", regeneráció: "bg-green-500/20 border-green-500/30 text-green-400" };
                        const c = colors[p.phase] || "bg-white/10 border-white/20 text-gray-400";
                        return (
                          <div key={`period-${idx}`} className={`flex-1 p-2 rounded-lg border text-center ${c}`} data-testid={`period-${idx}`}>
                            <p className="text-[10px] font-medium">{p.phase?.charAt(0).toUpperCase() + p.phase?.slice(1)}</p>
                            <p className="text-[9px] opacity-70">{p.week_range}. hét</p>
                            <p className="text-[9px] opacity-60 mt-0.5">{p.intensity}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Phase description */}
                {aiPlan.phase_description && (
                  <p className="text-[11px] text-gray-400 bg-white/5 rounded-lg p-2" data-testid="ai-plan-phase-desc">
                    Aktuális fázis: <span className="text-white">{aiPlan.phase}</span> – {aiPlan.phase_description}
                  </p>
                )}
                {aiPlan.tips && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Tippek</p>
                    <ul className="text-xs text-gray-300 space-y-1" data-testid="ai-plan-tips">
                      {aiPlan.tips.map((tip, idx) => (
                        <li key={`tip-${idx}`}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  data-testid="ai-plan-apply-btn"
                  onClick={handleApplyAiPlan}
                  disabled={aiPlanApplying || aiPlanLoading}
                  className="neon-btn w-full text-sm flex items-center justify-center gap-2 mt-2"
                >
                  {aiPlanApplying ? <Loader2 size={16} className="animate-spin" /> : <CalendarPlus size={16} />}
                  {aiPlanApplying ? "Hozzáadás folyamatban..." : "Hozzáadás a heti tervhez"}
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-4" data-testid="ai-plan-empty">Még nincs generált edzésterv</p>
            )}
          </GlassCard>

          <button
            data-testid="generate-workout-btn"
            onClick={generatePlan}
            disabled={generating}
            className="neon-btn w-full text-sm flex items-center justify-center gap-2"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Új edzésterv generálása
          </button>
        </TabsContent>

        {/* Session */}
        <TabsContent value="session" className="space-y-4 mt-4">
          <GlassCard testId="session-timer-card" className="fade-in-up">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Valós idejű session</p>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm text-gray-400">Eltelt idő</p>
                <p data-testid="session-timer-display" className="font-heading text-4xl text-white">{formatTime(sessionSeconds)}</p>
                <p className="text-xs text-gray-500 mt-1">{sessionMinutes} perc (kerekítve)</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {sessionStatus !== "running" && sessionStatus !== "paused" && (
                  <button data-testid="session-start-btn" onClick={handleSessionStart} className="neon-btn text-xs px-6 py-2 flex items-center gap-2">
                    <Play size={14} /> Start
                  </button>
                )}
                {sessionStatus === "running" && (
                  <button data-testid="session-pause-btn" onClick={handleSessionPause} className="neon-btn text-xs px-6 py-2 flex items-center gap-2">
                    <Pause size={14} /> Szünet
                  </button>
                )}
                {sessionStatus === "paused" && (
                  <button data-testid="session-resume-btn" onClick={handleSessionResume} className="neon-btn text-xs px-6 py-2 flex items-center gap-2">
                    <Play size={14} /> Folytatás
                  </button>
                )}
                {(sessionStatus === "running" || sessionStatus === "paused") && (
                  <button data-testid="session-stop-btn" onClick={handleSessionStop} className="px-6 py-2 rounded-full border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all text-xs flex items-center gap-2">
                    <Square size={14} /> Stop
                  </button>
                )}
                <button data-testid="session-reset-btn" onClick={handleSessionReset} className="px-6 py-2 rounded-full border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-all text-xs">
                  Reset
                </button>
              </div>
            </div>
          </GlassCard>

          {sessionSummary && (
            <GlassCard testId="session-summary-card" className="fade-in-up fade-in-up-1">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Session összefoglaló</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] text-gray-500">Időtartam</p>
                  <p data-testid="session-summary-duration" className="text-lg text-white">{sessionSummary.duration_min} perc</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Intenzitás</p>
                  <p data-testid="session-summary-intensity" className="text-lg text-white">{sessionForm.intensity}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">RPE</p>
                  <p data-testid="session-summary-rpe" className="text-lg text-white">{sessionForm.rpe}/10</p>
                </div>
              </div>
            </GlassCard>
          )}

          <GlassCard testId="session-form-card" className="fade-in-up fade-in-up-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Session mentése</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Edzés neve</Label>
                  <Input
                    data-testid="session-name-input"
                    value={sessionForm.name}
                    onChange={(e) => updateSessionField("name", e.target.value)}
                    placeholder="Pl. Esti futás"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Intenzitás</Label>
                  <select
                    data-testid="session-intensity-select"
                    value={sessionForm.intensity}
                    onChange={(e) => updateSessionField("intensity", e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    <option value="alacsony">Alacsony</option>
                    <option value="közepes">Közepes</option>
                    <option value="magas">Magas</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">RPE (érzet 1-10)</Label>
                <div className="flex items-center gap-4">
                  <input
                    data-testid="session-rpe-input"
                    type="range"
                    min="1"
                    max="10"
                    value={sessionForm.rpe}
                    onChange={(e) => updateSessionField("rpe", Number(e.target.value))}
                    className="w-full"
                  />
                  <span data-testid="session-rpe-value" className="text-xs text-white w-8 text-right">{sessionForm.rpe}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Megjegyzés</Label>
                <Input
                  data-testid="session-notes-input"
                  value={sessionForm.notes}
                  onChange={(e) => updateSessionField("notes", e.target.value)}
                  placeholder="Pl. pulzus, időjárás"
                  className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Gyakorlatok (soronként)</Label>
                <textarea
                  data-testid="session-exercises-input"
                  value={sessionForm.exercises}
                  onChange={(e) => updateSessionField("exercises", e.target.value)}
                  placeholder="Pl. 400m intervall\nLevezetés"
                  className="w-full min-h-[90px] bg-black/20 border border-white/10 text-white placeholder:text-gray-500 rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>

              <button
                data-testid="session-save-btn"
                onClick={handleSessionSave}
                disabled={sessionSaving}
                className="neon-btn w-full text-sm flex items-center justify-center gap-2"
              >
                {sessionSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Session mentése naplóba
              </button>
            </div>
          </GlassCard>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <GlassCard className="fade-in-up" testId="workout-log-form-card">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Valós idejű naplózás</p>
            <form onSubmit={handleLogSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Edzés neve</Label>
                  <Input
                    data-testid="workout-log-name-input"
                    value={logForm.name}
                    onChange={(e) => updateLogField("name", e.target.value)}
                    placeholder="Pl. Futás intervall"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Dátum</Label>
                  <Input
                    data-testid="workout-log-date-input"
                    type="date"
                    value={logForm.date}
                    onChange={(e) => updateLogField("date", e.target.value)}
                    className="bg-black/20 border-white/10 text-white focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Időtartam (perc)</Label>
                  <Input
                    data-testid="workout-log-duration-input"
                    type="number"
                    min="5"
                    step="5"
                    value={logForm.duration}
                    onChange={(e) => updateLogField("duration", e.target.value)}
                    placeholder="45"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Intenzitás</Label>
                  <select
                    data-testid="workout-log-intensity-select"
                    value={logForm.intensity}
                    onChange={(e) => updateLogField("intensity", e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  >
                    <option value="alacsony">Alacsony</option>
                    <option value="közepes">Közepes</option>
                    <option value="magas">Magas</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-xs">Megjegyzés</Label>
                  <Input
                    data-testid="workout-log-notes-input"
                    value={logForm.notes}
                    onChange={(e) => updateLogField("notes", e.target.value)}
                    placeholder="Pl. energia szint, tempo"
                    className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">RPE (érzet 1-10)</Label>
                <div className="flex items-center gap-4">
                  <input
                    data-testid="workout-log-rpe-input"
                    type="range"
                    min="1"
                    max="10"
                    value={logForm.rpe}
                    onChange={(e) => updateLogField("rpe", Number(e.target.value))}
                    className="w-full"
                  />
                  <span data-testid="workout-log-rpe-value" className="text-xs text-white w-8 text-right">{logForm.rpe}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Gyakorlatok (soronként)</Label>
                <textarea
                  data-testid="workout-log-exercises-input"
                  value={logForm.exercises}
                  onChange={(e) => updateLogField("exercises", e.target.value)}
                  placeholder="Pl. 400m intervall\nLevezetés futás"
                  className="w-full min-h-[90px] bg-black/20 border border-white/10 text-white placeholder:text-gray-500 rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <button
                  data-testid="workout-log-submit-btn"
                  type="submit"
                  disabled={logSaving}
                  className="neon-btn w-full text-sm flex items-center justify-center gap-2"
                >
                  {logSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  {editingLogId ? "Napló frissítése" : "Edzés naplózása"}
                </button>
                {editingLogId && (
                  <button
                    data-testid="workout-log-cancel-btn"
                    type="button"
                    onClick={resetLogForm}
                    className="w-full md:w-auto px-6 py-3 rounded-full border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all text-sm"
                  >
                    Mégse
                  </button>
                )}
              </div>
            </form>
          </GlassCard>

          <GlassCard testId="workout-summary-card" className="fade-in-up fade-in-up-1">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">{periodLabel} riport</p>
                <p className="text-[10px] text-gray-500">{periodRange}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="trend-toggle-weekly"
                  onClick={() => setTrendView("weekly")}
                  className={`px-3 py-1 rounded-full text-xs border ${trendView === "weekly" ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-400"}`}
                >
                  Heti
                </button>
                <button
                  data-testid="trend-toggle-monthly"
                  onClick={() => setTrendView("monthly")}
                  className={`px-3 py-1 rounded-full text-xs border ${trendView === "monthly" ? "border-[#00f260] text-[#00f260]" : "border-white/10 text-gray-400"}`}
                >
                  Havi
                </button>
                <button
                  data-testid="workout-report-copy-btn"
                  onClick={handleCopyReport}
                  className="text-xs text-gray-400 hover:text-white transition"
                >
                  Riport másolása
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] text-gray-500">Edzések</p>
                <p data-testid="summary-total-sessions" className="text-lg text-white">{totalSessions}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Összidő</p>
                <p data-testid="summary-total-minutes" className="text-lg text-white">{totalMinutes} perc</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Átlag RPE</p>
                <p data-testid="summary-avg-rpe" className="text-lg text-white">{avgRpe ? avgRpe.toFixed(1) : "-"}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Terhelés pont</p>
                <p data-testid="summary-load-score" className="text-lg text-white">{loadScore}</p>
              </div>
            </div>
          </GlassCard>

          <GlassCard testId="workout-load-zones-card" className="fade-in-up fade-in-up-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Terhelési zónák</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-xl bg-white/5" data-testid="load-zone-low">
                <p className="text-[10px] text-gray-500">Alacsony</p>
                <p className="text-lg text-white">{zoneCounts.low}</p>
                <p className="text-[10px] text-gray-500">0-199 pont</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5" data-testid="load-zone-medium">
                <p className="text-[10px] text-gray-500">Közepes</p>
                <p className="text-lg text-white">{zoneCounts.medium}</p>
                <p className="text-[10px] text-gray-500">200-399 pont</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5" data-testid="load-zone-high">
                <p className="text-[10px] text-gray-500">Magas</p>
                <p className="text-lg text-white">{zoneCounts.high}</p>
                <p className="text-[10px] text-gray-500">400+ pont</p>
              </div>
            </div>
          </GlassCard>

          <GlassCard testId="workout-rpe-chart-card" className="fade-in-up fade-in-up-3">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">RPE trend ({trendView === "monthly" ? "aktuális hónap" : "utóbbi 7 nap"})</p>
            {rpeTrend.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rpeTrend}>
                    <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} domain={[0, 10]} axisLine={false} tickLine={false} />
                    <Tooltip content={<RpeTooltip />} />
                    <Line type="monotone" dataKey="rpe" stroke="#00f260" strokeWidth={2} dot={{ r: 3, fill: "#00f260" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-gray-500" data-testid="rpe-empty">Nincs RPE adat ebben az időszakban</div>
            )}
          </GlassCard>

          <GlassCard testId="workout-log-filter-card" className="fade-in-up fade-in-up-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-[#00f260]" />
                <p className="text-xs text-gray-400 uppercase tracking-wider">Szűrők</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="workout-log-export-btn"
                  onClick={handleLogsExport}
                  className="text-xs text-[#00f260] hover:text-white transition"
                >
                  CSV export
                </button>
                <button
                  data-testid="workout-log-clear-filters"
                  onClick={() => {
                    setLogSearch("");
                    setLogIntensityFilter("all");
                    setLogDateFrom("");
                    setLogDateTo("");
                  }}
                  className="text-xs text-gray-400 hover:text-white transition"
                >
                  Törlés
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-400 text-xs">Keresés</Label>
                <Input
                  data-testid="workout-log-search-input"
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  placeholder="Edzés név, megjegyzés..."
                  className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Intenzitás</Label>
                <select
                  data-testid="workout-log-intensity-filter"
                  value={logIntensityFilter}
                  onChange={(e) => setLogIntensityFilter(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                >
                  <option value="all">Összes</option>
                  <option value="alacsony">Alacsony</option>
                  <option value="közepes">Közepes</option>
                  <option value="magas">Magas</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Dátum -tól</Label>
                <Input
                  data-testid="workout-log-date-from"
                  type="date"
                  value={logDateFrom}
                  onChange={(e) => setLogDateFrom(e.target.value)}
                  className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-xs">Dátum -ig</Label>
                <Input
                  data-testid="workout-log-date-to"
                  type="date"
                  value={logDateTo}
                  onChange={(e) => setLogDateTo(e.target.value)}
                  className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                />
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-3" data-testid="workout-log-results-count">
              Találat: {filteredLogs.length}
            </div>
          </GlassCard>

          <GlassCard className="fade-in-up fade-in-up-2" testId="workout-log-history-card">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Naplózott edzések</p>
            {logsLoading ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm" data-testid="workout-log-loading">
                <Loader2 size={16} className="animate-spin" />
                Betöltés...
              </div>
            ) : filteredLogs.length > 0 ? (
              <div className="space-y-3">
                {filteredLogs.map((log, i) => (
                  <div key={log.id} className="p-3 bg-white/5 rounded-xl" data-testid={`workout-log-item-${i}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white">{log.name}</p>
                        <p className="text-xs text-gray-400">{log.date} • {log.duration_min} perc</p>
                        {log.notes && <p className="text-xs text-gray-500 mt-1">{log.notes}</p>}
                        {log.rpe && <p className="text-[10px] text-gray-400 mt-1" data-testid={`workout-log-rpe-${log.id}`}>RPE: {log.rpe}/10</p>}
                      </div>
                      <Badge className={`${getIntensityColor(log.intensity)} bg-white/5 border-0 text-xs`}>
                        {log.intensity}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        data-testid={`workout-log-edit-${log.id}`}
                        onClick={() => handleLogEdit(log)}
                        className="px-3 py-1 rounded-full border border-white/10 text-gray-300 text-xs hover:text-white hover:border-white/30 transition-all"
                      >
                        Szerkesztés
                      </button>
                      <button
                        data-testid={`workout-log-delete-${log.id}`}
                        onClick={() => handleLogDelete(log.id)}
                        className="px-3 py-1 rounded-full border border-red-400/30 text-red-300 text-xs hover:text-red-200 hover:border-red-300 transition-all"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : logs.length > 0 ? (
              <div className="text-center py-6" data-testid="workout-log-empty">
                <p className="text-gray-400 text-sm">Nincs találat a szűrésre</p>
              </div>
            ) : (
              <div className="text-center py-6" data-testid="workout-log-empty">
                <p className="text-gray-400 text-sm">Még nincs naplózott edzés</p>
              </div>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
